import { Instagram, LinkedIn } from "@/components/icons";

export const primaryNavGroups = [
  {
    key: "membership",
    label: "Membership",
    href: "/membership",
    subItems: [],
  },
  {
    key: "knowledge",
    label: "Wissen",
    href: "/blog",
    subItems: [
      { label: "Wissensplattform", href: "/blog" },
      { label: "Interviews", href: "/interviews" },
      { label: "Schulungen", href: "/online-kurse" },
      { label: "Review myLoop", href: "/partner/ypsomed" },
      { label: "Review Dexcom", href: "/partner/dexcom" },
    ],
  },
  {
    key: "events",
    label: "Events",
    href: "/events",
    subItems: [
      { label: "Alle Events", href: "/events" },
      { label: "DiaTalks", href: "/events/diatalks" },
      { label: "TopicTalks", href: "/events/topictalks" },
      { label: "Sprechstunden", href: "/events/sprechstunden" },
    ],
  },
  {
    key: "reisen",
    label: "Reisen",
    href: "/events/reisen",
    subItems: [
      { label: "xAlps", href: "/events/dialetics-xalps-alpenueberquerung-mit-diabetes" },
      { label: "Zugspitze Gatterl", href: "/events/reisen/die-zugspitz-challenge-gatterl-route" },
      { label: "Zugspitze Reintal", href: "/events/reisen/die-zugspitz-challenge-reintal-route" },
    ],
  },
] as const;

/** Eintrag der dritten Ebene: ein Produkt-Hub aus dem Storyblok-Ordner productsReviews. */
export type NavLink = { label: string; href: string };

/** Unterpunkt einer Gruppe. Mit `children` klappt er eine weitere Ebene auf. */
type NavSubItem = NavLink & { children?: readonly NavLink[] };

export type NavGroup = {
  key: string;
  label: string;
  href: string;
  subItems: readonly NavSubItem[];
};

/** Label des Unterpunkts unter "Wissen", unter dem die Hubs erscheinen. */
const PRODUCT_REVIEWS_LABEL = "Produkt Reviews";

/**
 * Haengt die Produkt-Hubs als dritte Ebene unter "Wissen" › "Produkt Reviews".
 *
 * Ohne Hubs bleibt die Navigation unveraendert — der Punkt erscheint erst,
 * wenn im Ordner etwas veroeffentlicht ist (auf Staging: als Entwurf liegt).
 * Der Punkt selbst verlinkt nirgends hin, eine Uebersichtsseite /reviews gibt
 * es nicht; `href` dient nur als stabiler Schluessel.
 */
export function withProductHubs(hubs: readonly NavLink[]): readonly NavGroup[] {
  if (hubs.length === 0) return primaryNavGroups;

  return primaryNavGroups.map((group) =>
    group.key === "knowledge"
      ? {
          ...group,
          subItems: [
            ...group.subItems,
            { label: PRODUCT_REVIEWS_LABEL, href: "/reviews", children: hubs },
          ],
        }
      : group,
  );
}

/**
 * Ist dieser Menuepunkt fuer den aktuellen Pfad der aktive?
 *
 * Ein reines `pathname.startsWith(group.href)` markiert zwei Punkte
 * gleichzeitig, weil "Reisen" (/events/reisen) unterhalb von "Events"
 * (/events) liegt: Auf einer Reise-Seite trifft das Praefix auf beide zu.
 *
 * Deshalb gewinnt der laengste passende `href` — der speziellere Punkt
 * verdraengt den allgemeineren. Fuer alle anderen Gruppen aendert sich
 * nichts, da sich ihre Pfade nicht ueberschneiden.
 *
 * Die Reise-Unterseiten liegen zudem nicht alle unter /events/reisen:
 * xAlps haengt direkt unter /events/. Ohne die `subItems`-Pruefung wuerde
 * dort "Events" fett statt "Reisen".
 *
 * Die dritte Ebene zaehlt mit: Auf einem Produkt-Hub (/reviews/...) ist
 * "Wissen" aktiv.
 */
export function isNavGroupActive(
  group: NavGroup,
  pathname: string,
  groups: readonly NavGroup[] = primaryNavGroups,
): boolean {
  const matches = (href: string) =>
    pathname === href || pathname.startsWith(href + "/");

  /** Laengster Treffer einer Gruppe — ueber ihren eigenen href, ihre
   *  Unterpunkte und deren Kinder. Unterpunkte zaehlen mit, weil nicht alle
   *  unterhalb des Gruppenpfads liegen (xAlps haengt direkt unter /events/). */
  const score = (g: NavGroup) => {
    const hrefs = [
      g.href,
      ...g.subItems.flatMap((i) => [i.href, ...(i.children ?? []).map((c) => c.href)]),
    ];
    const treffer = hrefs.filter(matches).map((h) => h.length);
    return treffer.length ? Math.max(...treffer) : 0;
  };

  const eigen = score(group);
  if (eigen === 0) return false;

  // Passt eine andere Gruppe spezifischer, gehoert der Pfad dorthin.
  return !groups.some(
    (other) => other.key !== group.key && score(other) > eigen,
  );
}

export const secondaryLinks = [
  { label: "Über uns", href: "/about" },
  { label: "Kontakt", href: "/kontakt" },
  { label: "Partner-Praxen", href: "/praxen" },
  { label: "B2B", href: "/partner/b2b" },
] as const;

export const SOCIAL_LINKS = [
  {
    href: "https://www.instagram.com/dialetics_diabetes",
    label: "Instagram",
    Icon: Instagram,
  },
  {
    href: "https://www.linkedin.com/company/dialetics-de/",
    label: "LinkedIn",
    Icon: LinkedIn,
  },
] as const;
